/**
 * How the workspace arranges its grid and chart: the preference, where it's kept, and every
 * style that differs between the two arrangements.
 *
 * A per-user view preference rather than part of a saved report, so it lives in
 * localStorage.
 */
import { LAYOUT_STORAGE_KEY } from '../config/SummaryReportsConfig.tsx';

export type SummaryReportLayout = 'sideBySide' | 'stacked';

export const DEFAULT_LAYOUT: SummaryReportLayout = 'sideBySide';

const VALID_LAYOUTS: readonly SummaryReportLayout[] = ['sideBySide', 'stacked'];

export function isSummaryReportLayout(value: unknown): value is SummaryReportLayout {
    return VALID_LAYOUTS.includes(value as SummaryReportLayout);
}

/** Reads the stored layout, defaulting for anything unexpected or unreadable. */
export function readStoredLayout(): SummaryReportLayout {
    try {
        const stored = localStorage.getItem(LAYOUT_STORAGE_KEY);
        return isSummaryReportLayout(stored) ? stored : DEFAULT_LAYOUT;
    } catch {
        return DEFAULT_LAYOUT;
    }
}

/** Persists the choice. A storage failure just means it won't stick. */
export function storeLayout(layout: SummaryReportLayout): void {
    try {
        localStorage.setItem(LAYOUT_STORAGE_KEY, layout);
    } catch {
        // Private browsing or quota — keep the in-memory choice.
    }
}

// ─── Styling ─────────────────────────────────────────────────────────────────

/**
 * The single gap in the workspace: between grid and chart, and between either and the edge.
 * Shared by both arrangements so the grid keeps its surroundings when the chart moves.
 */
const PANE_GAP = '16px';

/**
 * The chart pane's own inset, on top of the shared gap.
 *
 * The chart is a card in its own right — border, header rule, plot to the edges — so it
 * reads better standing slightly off the grid. The grid has its own chrome and needs less.
 */
const CHART_PANE_PADDING = '8px';

/**
 * How far short of the content area the chart pane stops, so it doesn't sit on the fold.
 *
 * The pane is sized from its container, and that container is already the viewable area: the
 * view is a `100vh` column, the header takes its share, and the rest is `flex: 1`.
 */
const CHART_PANE_HEIGHT_INSET = '24px';

/** The chart pane's default height: a screenful, less the inset. A typed height still wins. */
const CHART_PANE_HEIGHT = `calc(100% - ${CHART_PANE_HEIGHT_INSET})`;

/**
 * Scrolling for the content area, identical in both arrangements.
 *
 * Stacked scrolls and side-by-side has nothing to scroll, but the gutter is reserved either
 * way — letting a scrollbar appear on the toggle would reflow everything inside it.
 */
const viewContent = {
    overflowY: 'scroll',
    overflowX: 'hidden',
    scrollbarGutter: 'stable',
} as const;

/** How long the chart pane takes to grow or collapse. */
export const CHART_PANE_TRANSITION_MS = 220;

const chartTransition = {
    transition: `width ${CHART_PANE_TRANSITION_MS}ms ease, height ${CHART_PANE_TRANSITION_MS}ms ease, opacity ${CHART_PANE_TRANSITION_MS}ms ease`,
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
} as const;

/**
 * Every style that differs between the two arrangements. Callers spread these alongside the
 * static styling they own.
 *
 * Both render the same DOM tree, so nothing remounts on a switch. That matters:
 * GridChartsRendererProxy deregisters its chart on unmount, and reparenting it would cost
 * the user their chart type and configuration.
 */
export const LAYOUT_SX = {
    // Grid left, chart right, both filling the height.
    sideBySide: {
        viewContent,
        gridPaper: { overflow: 'hidden' },
        // Padding here rather than a margin on the grid, so one box spaces both children and
        // the chart arriving or leaving never shifts the grid.
        container: {
            flex: 1,
            minHeight: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            gap: PANE_GAP,
            p: PANE_GAP,
            boxSizing: 'border-box',
            background: 'transparent',
        },
        grid: { flex: 1, minWidth: 0, borderRadius: 2 },
        // No height needed: a flex item stretches to the container, which is the content
        // area, so the pane already stops at the fold.
        chartOpen: {
            width: '50%',
            minWidth: 0,
            overflow: 'hidden',
            position: 'relative',
            p: CHART_PANE_PADDING,
            boxSizing: 'border-box',
            ...chartTransition,
        },
        chartCollapsed: { width: 0, minWidth: 0, overflow: 'hidden', ...chartTransition },
    },
    // Grid fills the visible area; the chart hangs below it, extending the scroll region.
    stacked: {
        // The chart sits past the bottom of the Paper around the grid, so that Paper has to
        // let it through for the page scroll to reach it.
        viewContent,
        gridPaper: { overflow: 'visible' },
        // Block, not flex. `flex: 1` gives this a definite height, which is what the grid's
        // `height: 100%` resolves against — against an indefinite one it computes to auto,
        // and DataGrid has no intrinsic height, so it would collapse. Block also keeps the
        // chart out of flex height distribution: it stacks below and overflows, which is
        // what extends the scrollable area.
        container: {
            width: '100%',
            flex: 1,
            minHeight: 0,
            overflow: 'visible',
            p: PANE_GAP,
            boxSizing: 'border-box',
        },
        grid: { height: '100%' },
        // A second screenful below the grid, so scrolling down lands on a full chart rather
        // than a strip of one.
        chartOpen: {
            width: '100%',
            height: CHART_PANE_HEIGHT,
            position: 'relative',
            mt: PANE_GAP,
            p: CHART_PANE_PADDING,
            boxSizing: 'border-box',
            ...chartTransition,
        },
        chartCollapsed: { height: 0, overflow: 'hidden', ...chartTransition },
    },
} as const;
